import { CacheTimes, CacheUtility } from '../cache';
import { CommonUtility } from '../common';
import { APIPath, cacheStorageKeys } from '../constants';
import { BaseService } from './base';
import { CRUDService } from './crud.ts';

class Billing extends CRUDService {
  constructor() {
    super(APIPath.billingBaseTemplate);
  }

  async metadata() {
    const cacheData = CacheUtility.get(cacheStorageKeys.billingMetadata);

    if (cacheData) {
      return cacheData;
    }

    const data = await BaseService.get(APIPath.billingMetadata);
    CacheUtility.save(cacheStorageKeys.billingMetadata, data, CacheTimes.TWO_HOURS);
    return data;
  }

  createCustomerTempate(data) {
    return BaseService.post(APIPath.billingCustomerTemplates, data);
  }

  updateCustomerTempate(id, data) {
    return BaseService.put(`${APIPath.billingCustomerTemplates}/${id}`, data);
  }

  getCustomerTempate(id) {
    return BaseService.get(`${APIPath.billingCustomerTemplates}/${id}`);
  }

  getInvoices(params) {
    return BaseService.get(
      `${APIPath.billingInvoices}/?${CommonUtility.objectToParams(params)}`,
    );
  }

  getInvoicesLogs(id, params) {
    return BaseService.get(
      `${APIPath.billingInvoices}/${id}/logs/?${CommonUtility.objectToParams(params)}`,
    );
  }

  getDraft(params) {
    return BaseService.get(
      `${APIPath.billingDraft}/?${CommonUtility.objectToParams(params)}`,
    );
  }

  addLineItem(id, payload) {
    return BaseService.post(`${APIPath.billingInvoices}/${id}/line-items`, payload);
  }

  updateLineItem(invoiceId, lineItemId, payload) {
    return BaseService.patch(
      `${APIPath.billingInvoices}/${invoiceId}/line-items/${lineItemId}`,
      payload,
    );
  }

  deleteLineItem(invoiceId, lineItemId) {
    return BaseService.remove(
      `${APIPath.billingInvoices}/${invoiceId}/line-items/${lineItemId}`,
    );
  }

  markInvoiceAsOpen(invoiceId) {
    return BaseService.patch(`${APIPath.billingInvoices}/${invoiceId}/status/open`);
  }

  exportInvoiceQuickbook(invoiceId) {
    return BaseService.get(`${APIPath.billingInvoices}/${invoiceId}/quickbooksExport`);
  }

  getInvoice(invoiceId) {
    return BaseService.get(`${APIPath.billingInvoices}/${invoiceId}`);
  }

  getInvoicePDF(invoiceId, config, header) {
    return BaseService.get(
      `${APIPath.billingInvoices}/${invoiceId}/pdf`,
      false,
      true,
      null,
      null,
      config,
      header,
    );
  }

  updateInvoice(invoiceId, data) {
    return BaseService.patch(`${APIPath.billingInvoices}/${invoiceId}`, data);
  }

  updateInvoiceInsignificant(invoiceId, data) {
    return BaseService.patch(`${APIPath.billingInvoices}/${invoiceId}/details`, data);
  }

  getCustomerDetail(id) {
    return BaseService.get(`${APIPath.customers}/${id}`);
  }

  markInvoicePreApproved(invoiceId) {
    return BaseService.patch(`${APIPath.billingInvoices}/${invoiceId}/status/preApprove`);
  }

  updateMarginRate(data) {
    return BaseService.put(APIPath.billingLabelRate, data);
  }

  getMarginRate(params) {
    return BaseService.get(
      `${APIPath.billingLabelRate}/?${CommonUtility.objectToParams(params)}`,
    );
  }

  exportInvoice(id) {
    return BaseService.get(`${APIPath.billingInvoices}/${id}/csv`);
  }

  archiveInvoice(id) {
    return BaseService.remove(`${APIPath.billingInvoices}/${id}`);
  }

  quickBookConfig() {
    return BaseService.get(APIPath.billingQuickbookConfig);
  }

  deleteQuickBookConfig(params) {
    return BaseService.remove(
      `${APIPath.billingQuickbookConfig}/?${CommonUtility.objectToParams(params)}`,
    );
  }

  updateQuickBookConfig(data) {
    return BaseService.put(APIPath.billingQuickbookConfig, data);
  }

  exportQuickBook(data) {
    return BaseService.post(APIPath.billingInvoiceBulkQuickBookExport, data);
  }

  getCustomerHoldStatus(id) {
    return BaseService.get(`${APIPath.customerHoldStatus}/?customerOrganizationId=${id}`);
  }

  updateCustomerHoldStatus(data) {
    return BaseService.put(APIPath.customerHoldStatus, data);
  }

  removeCustomerHold(data) {
    return BaseService.deleteWithBody(APIPath.customerHoldStatus, data);
  }
}

const BillingService = new Billing();
Object.freeze(BillingService);
export { BillingService };
